import { activateSieveScript, createSieveScript, getSieveScript, listSieveScripts, updateSieveScript, } from "@apps/client-sdk";
import { supportsExtension } from "./builder/capabilities.js";
import { el } from "./dom.js";
import { MAIN_SCRIPT_NAME, VACATION_SCRIPT_NAME, createMainWrapper, generateVacationScript, isVacationIncludeEnabled, parseVacationScript, setVacationInclude, } from "./vacationInclude.js";
export function renderVacationPanel(options) {
    const panel = el("div", "sieve-vacation");
    if (!supportsExtension(options.capabilities, "vacation")) {
        panel.append(el("p", "flash flash-error", "This mail server does not advertise the vacation Sieve extension."));
        return panel;
    }
    if (!supportsExtension(options.capabilities, "include")) {
        panel.append(el("p", "flash flash-error", "Vacation quick toggle needs the include extension. Use Scripting → Source to write a vacation script manually."));
        return panel;
    }
    panel.append(el("p", "hint", 'Auto-responder is stored in a separate “vacation” script and turned on by including it from your active filter script.'));
    const enableLabel = el("label", "sieve-check sieve-vacation-enable");
    const enableToggle = el("input");
    enableToggle.type = "checkbox";
    enableLabel.append(enableToggle, document.createTextNode(" Enable vacation / auto-responder"));
    panel.append(enableLabel);
    const subject = el("input", "input");
    subject.type = "text";
    subject.placeholder = "Out of office";
    const days = el("input", "input");
    days.type = "number";
    days.min = "1";
    days.value = "7";
    const addresses = el("input", "input");
    addresses.type = "text";
    addresses.placeholder = "you@example.com (optional, comma-separated)";
    const message = el("textarea", "input");
    message.rows = 8;
    message.placeholder = "I am away from email until…";
    message.style.minHeight = "8rem";
    message.style.fontFamily = "inherit";
    const form = el("div", "sieve-vacation-form mt");
    const addField = (label, node) => {
        const row = el("label", "sieve-field");
        row.append(el("span", "sieve-field-label", label), node);
        form.append(row);
    };
    addField("Subject", subject);
    addField("Days between replies", days);
    addField("Reply addresses", addresses);
    addField("Message", message);
    panel.append(form);
    const saveBtn = el("button", "btn btn-primary btn-sm mt", "Save vacation settings");
    saveBtn.type = "button";
    panel.append(saveBtn);
    const statusLine = el("p", "muted mt", "Loading…");
    panel.append(statusLine);
    let loading = false;
    const setBusy = (busy) => {
        loading = busy;
        enableToggle.disabled = busy;
        subject.disabled = busy;
        days.disabled = busy;
        addresses.disabled = busy;
        message.disabled = busy;
        saveBtn.disabled = busy;
    };
    const readForm = () => ({
        subject: subject.value,
        message: message.value,
        days: Number(days.value) || 7,
        addresses: addresses.value
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
    });
    const writeForm = (settings) => {
        subject.value = settings.subject;
        message.value = settings.message;
        days.value = String(settings.days || 7);
        addresses.value = settings.addresses.join(", ");
    };
    const load = async () => {
        setBusy(true);
        statusLine.textContent = "Loading…";
        try {
            const list = await listSieveScripts(options.storage);
            let enabled = false;
            if (list.active) {
                const active = await getSieveScript(options.storage, list.active);
                enabled = isVacationIncludeEnabled(active.content);
            }
            enableToggle.checked = enabled;
            if (list.scripts.includes(VACATION_SCRIPT_NAME)) {
                const vac = await getSieveScript(options.storage, VACATION_SCRIPT_NAME);
                const parsed = parseVacationScript(vac.content);
                if (parsed)
                    writeForm(parsed);
            }
            else {
                writeForm({
                    subject: "Out of office",
                    message: "I am currently away from email and will respond when I return.",
                    days: 7,
                    addresses: [],
                });
            }
            statusLine.textContent = enabled ? "Vacation is enabled." : "Vacation is disabled.";
        }
        catch (error) {
            statusLine.textContent = "";
            options.showStatus(error instanceof Error ? error.message : "Failed to load vacation settings", "error");
        }
        finally {
            setBusy(false);
        }
    };
    const ensureScript = async (name, content) => {
        const list = await listSieveScripts(options.storage);
        if (list.scripts.includes(name)) {
            await updateSieveScript(options.storage, name, content);
        }
        else {
            await createSieveScript(options.storage, name, content);
        }
    };
    const apply = async (enable) => {
        setBusy(true);
        try {
            const settings = readForm();
            await ensureScript(VACATION_SCRIPT_NAME, generateVacationScript(settings));
            const list = await listSieveScripts(options.storage);
            let activeName = list.active;
            if (!activeName) {
                await ensureScript(MAIN_SCRIPT_NAME, createMainWrapper(enable));
                await activateSieveScript(options.storage, MAIN_SCRIPT_NAME);
                activeName = MAIN_SCRIPT_NAME;
            }
            else {
                const active = await getSieveScript(options.storage, activeName);
                const next = setVacationInclude(active.content, enable);
                await updateSieveScript(options.storage, activeName, next);
            }
            enableToggle.checked = enable;
            statusLine.textContent = enable ? "Vacation is enabled." : "Vacation is disabled.";
            options.showStatus(enable ? "Vacation enabled" : "Vacation disabled", "success");
        }
        catch (error) {
            options.showStatus(error instanceof Error ? error.message : "Failed to update vacation", "error");
        }
        finally {
            setBusy(false);
        }
    };
    enableToggle.addEventListener("change", () => {
        if (loading)
            return;
        void apply(enableToggle.checked);
    });
    saveBtn.addEventListener("click", () => {
        if (loading)
            return;
        void apply(enableToggle.checked);
    });
    void load();
    return panel;
}
